import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../lib/auth-context.js";
import { listBills } from "../../lib/api-modules/bills.js";
import { listIncomeEvents } from "../../lib/api-modules/income-events.js";
import { listSpendingTransactions } from "../../lib/api-modules/spending-transactions.js";
import {
  updateFireAssumptions,
  type UpdateFireAssumptionsInput,
} from "../../lib/api-modules/households.js";
import { fireNumbers, savingsRate, timeToFI } from "../../lib/util/fire.js";
import {
  socialSecurityEstimate,
  socialSecurityNpvAtRetirement,
} from "../../lib/util/social-security.js";
import {
  centsToDollars,
  dollarsToCents,
  formatCentsCompact,
} from "../../lib/util/money.js";
import { useToast } from "../toast.js";
import { Button, Card, NumberField } from "../design-system/index.js";

/**
 * Phase 6 Session 4 — Social Security tile. Manual PIA input (from
 * SSA "my Statement"), claim-age slider (62-70), live readout of
 * annual benefit + NPV offset to the FIRE target.
 *
 * **Persistence**: PIA + claim age save via PATCH to
 * `/v1/households/{id}/fire-assumptions`. The Save button is shown
 * when the user has edited; "Save" persists; otherwise live-readouts
 * track the on-screen state.
 */

const DEFAULT_CLAIM_AGE = 67;
const DEFAULT_FRA = 67;
const DEFAULT_RETIREMENT_AGE = 65;
const DEFAULT_RETURN_PCT = 5;
const DEFAULT_WR_PCT = 4;

interface Props {
  readonly hideValues: boolean;
  readonly fiAssetsCents: number;
}

export function SocialSecurityTile({
  hideValues,
  fiAssetsCents,
}: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const trailingFrom = useMemo(() => {
    const d = new Date(today);
    d.setUTCMonth(d.getUTCMonth() - 12);
    return d.toISOString().slice(0, 10);
  }, [today]);

  const billsQuery = useQuery({
    queryKey: ["bills", { includeArchived: false }],
    queryFn: () => listBills(false),
  });
  const incomeQuery = useQuery({
    queryKey: ["income-events", { from: trailingFrom }],
    queryFn: () => listIncomeEvents({ from: trailingFrom }),
  });
  const spendQuery = useQuery({
    queryKey: ["spending-transactions", { from: trailingFrom, to: today }],
    queryFn: () =>
      listSpendingTransactions({ from: trailingFrom, to: today }),
  });

  const a = user?.fire_assumptions;
  const persistedPiaCents = a?.social_security_pia_cents;
  const persistedClaimAge = a?.social_security_claim_age ?? DEFAULT_CLAIM_AGE;
  const retirementAge = a?.retirement_target_age ?? DEFAULT_RETIREMENT_AGE;

  const [piaInput, setPiaInput] = useState(
    persistedPiaCents !== undefined ? centsToDollars(persistedPiaCents) : "",
  );
  const [claimAge, setClaimAge] = useState(persistedClaimAge);

  const piaCents = piaInput === "" ? 0 : dollarsToCents(piaInput);

  const benefitAnnualCents = useMemo(() => {
    if (piaCents <= 0) return 0;
    try {
      return socialSecurityEstimate({
        pia_at_fra_cents: piaCents,
        claim_age: claimAge,
        fra_age: DEFAULT_FRA,
      });
    } catch {
      return 0;
    }
  }, [piaCents, claimAge]);

  const isLoading = billsQuery.isLoading || incomeQuery.isLoading || spendQuery.isLoading;

  const computed = useMemo(() => {
    if (isLoading || piaCents <= 0) return null;
    const sr = savingsRate({
      income_events: incomeQuery.data?.income_events ?? [],
      bills: billsQuery.data?.bills ?? [],
      spending_transactions: spendQuery.data?.spending_transactions ?? [],
      today,
      lookback_months: 12,
    });
    if (sr.gross_income_cents <= 0) return null;
    const annualSpendCents =
      a?.annual_spend_override_cents ?? sr.annualized_spend_cents;
    const wrPct = a?.withdrawal_rate_pct ?? DEFAULT_WR_PCT;
    const returnPct = a?.expected_return_rate_pct ?? DEFAULT_RETURN_PCT;

    const numbers = fireNumbers({
      annual_spend_cents: annualSpendCents,
      withdrawal_rate_pct: wrPct,
    });

    const ssNpv = socialSecurityNpvAtRetirement({
      pia_at_fra_cents: piaCents,
      claim_age: claimAge,
      fra_age: DEFAULT_FRA,
      retirement_target_age: retirementAge,
      expected_return_rate_pct: returnPct,
    });

    const baselineTime = timeToFI({
      current_net_worth_cents: fiAssetsCents,
      monthly_net_savings_cents: sr.monthly_net_savings_cents,
      annual_return_rate_pct: returnPct,
      fire_target_cents: numbers.regular_cents,
      today,
    });
    const offsetTime = timeToFI({
      current_net_worth_cents: fiAssetsCents,
      monthly_net_savings_cents: sr.monthly_net_savings_cents,
      annual_return_rate_pct: returnPct,
      fire_target_cents: Math.max(0, numbers.regular_cents - ssNpv),
      today,
    });

    return {
      ssNpvCents: ssNpv,
      fireTargetCents: numbers.regular_cents,
      adjustedTargetCents: Math.max(0, numbers.regular_cents - ssNpv),
      baselineYearsToFI: baselineTime.years_to_fi,
      offsetYearsToFI: offsetTime.years_to_fi,
      offsetFiDate: offsetTime.fi_date,
    };
  }, [
    isLoading,
    piaCents,
    claimAge,
    today,
    a,
    billsQuery.data,
    incomeQuery.data,
    spendQuery.data,
    fiAssetsCents,
    retirementAge,
  ]);

  const dirty =
    piaInput !==
      (persistedPiaCents !== undefined ? centsToDollars(persistedPiaCents) : "") ||
    claimAge !== persistedClaimAge;

  const mutation = useMutation({
    mutationFn: (input: UpdateFireAssumptionsInput) => {
      if (!user) throw new Error("Not signed in");
      return updateFireAssumptions(user.primary_household_id, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.show("Social Security inputs saved.");
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Failed to save";
      toast.show(msg);
    },
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    mutation.mutate({
      social_security_pia_cents: piaInput === "" ? null : piaCents,
      social_security_claim_age: claimAge,
    });
  }

  const yearsDelta =
    computed &&
    computed.baselineYearsToFI !== null &&
    computed.offsetYearsToFI !== null
      ? computed.baselineYearsToFI - computed.offsetYearsToFI
      : null;

  return (
    <Card className="mb-6" data-testid="social-security-tile">
      <div className="mb-4">
        <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
          Social Security
        </p>
        <p className="mt-0.5 text-xs text-[var(--color-muted)]">
          Enter your PIA from{" "}
          <a
            href="https://www.ssa.gov/myaccount/"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-[var(--color-accent)]"
          >
            ssa.gov/myaccount
          </a>{" "}
          → claim-age slider re-projects how SS shifts your FIRE target.
        </p>
      </div>

      <form onSubmit={onSubmit} className="mb-4 grid gap-3 md:grid-cols-3">
        <NumberField
          label="PIA at FRA (annual)"
          helper="from SSA my Statement"
          value={piaInput}
          onChange={(e) => setPiaInput(e.target.value)}
          step="0.01"
          min="0"
          placeholder="e.g. 30000"
        />
        <div>
          <label
            htmlFor="ss-claim-age"
            className="mb-1 block text-xs font-medium text-[var(--color-muted)]"
          >
            Claim age:{" "}
            <span className="font-mono tabular-nums text-[var(--color-text)]">
              {claimAge}
            </span>
          </label>
          <input
            id="ss-claim-age"
            type="range"
            min="62"
            max="70"
            step="1"
            value={claimAge}
            onChange={(e) => setClaimAge(Number(e.target.value))}
            className="w-full accent-[var(--color-accent)]"
          />
          <p className="mt-1 text-[11px] text-[var(--color-muted)]">
            62 → 70% PIA · 67 (FRA) → 100% · 70 → 124%
          </p>
        </div>
        <div className="flex items-end">
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={!dirty || mutation.isPending}
          >
            {mutation.isPending ? "Saving…" : dirty ? "Save" : "Saved"}
          </Button>
        </div>
      </form>

      {piaCents <= 0 ? (
        <p className="rounded-md border border-dashed border-[var(--color-border-default)] p-6 text-center text-sm text-[var(--color-muted)]">
          Enter your PIA to see how Social Security shifts your FIRE target.
        </p>
      ) : (
        <div className="grid gap-3 md:grid-cols-3">
          <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
              Annual benefit at claim
            </p>
            <p className="mt-1 font-mono text-2xl font-medium tabular-nums text-[var(--color-text)]">
              {formatCentsCompact(benefitAnnualCents, { hidden: hideValues })}
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--color-muted)]">
              claim age {claimAge}
            </p>
          </div>
          <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
              FIRE target offset (NPV)
            </p>
            <p className="mt-1 font-mono text-2xl font-medium tabular-nums text-[var(--color-text)]">
              {computed
                ? formatCentsCompact(computed.ssNpvCents, { hidden: hideValues })
                : "—"}
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--color-muted)]">
              discounted at {a?.expected_return_rate_pct ?? DEFAULT_RETURN_PCT}% real
            </p>
          </div>
          <div className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3">
            <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
              Time-to-FI shift
            </p>
            <p className="mt-1 font-mono text-2xl font-medium tabular-nums text-[var(--color-text)]">
              {yearsDelta === null
                ? "—"
                : `−${yearsDelta.toFixed(1)} yr`}
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--color-muted)]">
              {computed?.offsetFiDate
                ? `target ${computed.offsetFiDate.slice(0, 7)}`
                : "—"}
            </p>
          </div>
        </div>
      )}

      {computed && yearsDelta !== null && yearsDelta > 0.5 && (
        <div className="mt-4 rounded-md bg-[var(--color-accent-soft)] px-4 py-3 text-sm text-[var(--color-accent)]">
          Social Security offsets{" "}
          <strong>
            {formatCentsCompact(computed.ssNpvCents, { hidden: hideValues })}
          </strong>{" "}
          of your FIRE target — Time-to-FI shifts{" "}
          <strong>{yearsDelta.toFixed(1)} years earlier</strong>.
        </div>
      )}
    </Card>
  );
}
